<template>
  <a
    class="d-user-list-entry"
    :class="{ 'just-added': isNewFollowing,
              'processing': isProcessing }"
    :href="userUrl"
    :title="entryTitle"
    @click="followOrUnfollowUser"
  >
    <span class="avatar">
      <DUserLinkPopup
        :user-id="user.id"
        :in-modal="true"
      >
        <template slot="content">
          <img :src="user.avatar_url">
        </template>
      </DUserLinkPopup>

      <DuiIconPlus
        v-if="allowAdding && !isNewFollowing"
        class="icon-sm icon-circle background-yellow-base icon-white"
      />

      <DuiIconCross
        v-if="isNewFollowing"
        class="icon-sm icon-circle background-black-base icon-white"
      />
    </span>

    <span class="info">
      <DUserLinkPopup
        :user-id="user.id"
        :in-modal="true"
      >
        <template slot="content">
          <span class="name">
            {{ user.firstname }} {{ user.lastname }}
          </span>
        </template>
      </DUserLinkPopup>

      <span class="user-info">
        {{ userInfo }}
        <span v-if="allowRemoval">
          -
          <span
            class="grid-item-shrink remove-user"
            :title="`Unfollow ${user.firstname }`"
            @click.stop.prevent="removeUser"
          >
            unfollow
          </span>
        </span>
      </span>
    </span>

    <DuiLoadingIndicator
      v-if="isProcessing"
    />
  </a>
</template>

<script>
import Vue from 'vue';

import followersService from '@d-services/followers-service/followers-service';
import DUserLinkPopup from '@d-components/d-user-link-popup/d-user-link-popup.vue';
import { getErrorMessage } from '@d-shared/utils/errors';
import URLS from '@d-shared/urls/urls';

const LABELS = Object.freeze({
  follow: 'Follow',
  following: 'FOLLOWING',
  unfollow: 'Unfollow',
});

export default {
  components: {
    DUserLinkPopup,
  },
  props: {
    allowAdding: {
      type: Boolean,
      default: false,
    },
    allowRemoval: {
      type: Boolean,
      default: false,
    },
    isFollowed: {
      type: Boolean,
      default: false,
    },
    removeAfterAdding: {
      type: Boolean,
      default: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    entryTitle: '',
    isNewFollowing: false,
    isProcessing: false,
    userInfo: null,
    userUrl: null,
  }),
  mounted() {
    this.$set(this, 'isNewFollowing', this.isFollowed);
    this.setupUser();
  },
  methods: {
    followOrUnfollowUser(ev) {
      if (!this.allowAdding) {
        return;
      }

      ev.preventDefault();

      if (this.isNewFollowing) {
        this.removeUser();
        return;
      }

      this.followUser();
    },

    async followUser() {
      if (!this.allowAdding) {
        return;
      }

      this.$set(this, 'isProcessing', true);

      try {
        await followersService.followUser(this.user.id);
        this.$set(this, 'isNewFollowing', true);
        this.setupUser();
        this.$emit('followed', this.user);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isProcessing', false);
    },

    async removeUser() {
      if (!this.allowRemoval && !this.isNewFollowing) {
        return;
      }

      this.$set(this, 'isProcessing', true);

      try {
        await followersService.stopFollowingUser(this.user.id);
        this.$set(this, 'isNewFollowing', false);
        this.setupUser();
        this.$emit('unfollowed', this.user);

        if (this.removeAfterAdding) {
          this.$emit('remove', this.user);
        }
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isProcessing', false);
    },
    setupUser() {
      if (this.allowAdding && this.isNewFollowing) {
        this.$set(this, 'entryTitle', `${ LABELS.unfollow } ${ this.user.firstname }`);
        this.$set(this, 'userInfo', LABELS.following);
        this.$set(this, 'userUrl', null);
        return;
      }

      const entryTitle = this.allowAdding ?
        `${ LABELS.follow } ${ this.user.firstname }` :
        `${ this.user.firstname } ${ this.user.lastname }`;
      this.$set(this, 'entryTitle', entryTitle);
      const userInfo = this.user.tag_line ? this.user.tag_line : this.user.type;
      this.$set(this, 'userInfo', userInfo);
      const userUrl = URLS.app.userProfile.entry.replace('<username>', this.user.username);
      this.$set(this, 'userUrl', userUrl);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.d-user-list-entry {
  align-items: center;
  background-color: getColor($white);
  color: getColor($black);
  display: grid;
  font-weight: normal;
  grid-column-gap: $_2em;
  grid-template-columns: $_6em auto;
  padding: $_1em;
  position: relative;

  @include transition((
    background-color
  ));

  &.processing,
  &.just-added {
    .avatar img,
    .info {
      opacity: $disabled-opacity;
    }
  }

  &.processing {
    pointer-events: none;
  }

  &:hover {
    background-color: getColor($grey, base);
    cursor: pointer;
  }

  .avatar {
    height: $_6em;
    position: relative;
    width: $_6em;

    img {
      border-radius: 50%;
      display: block;
      height: 100%;
      width: 100%;
    }

    .dui-icon {
      position: absolute;
      right: -$_1em;
      top: -$_1em;
      z-index: 1;
    }
  }

  .info {
    display: flex;
    flex-direction: column;
    width: 100%;

    .name {
      font-family: $alt-font;
      font-size: $font-size-lg;
    }

    .user-info {
      color: getColor($grey, dark);
      font-size: $font-size-xs;
    }

    .remove-user {
      font-size: $font-size-sm;
      font-weight: $base-weight-bold;

      @include transition((
        color
      ));

      &:hover {
        color: getColor($black);
      }
    }
  }

  .dui-loading-indicator {
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate3d(-50%, -50%, 0);
    z-index: 1;
  }
}
</style>
